import { useEffect, useState } from "react";
import { createClient, Session } from "@supabase/supabase-js";
import { usePaths } from "BEDROCK/paths";
import { useLocalStorage } from "PosterEditor/hooks/use-locale-storage";

const key = process.env.REACT_APP_SUPABASE_KEY;
const project = process.env.REACT_APP_SUPABASE_PROJECT;

const supabaseUrl = `https://${project}.supabase.co`;
const supabaseAnonKey = key!; // use ! if you're sure it's defined, or add a check

export interface UserProfile {
  role: string;
  marked_for_removal: string | null;
}

const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

type Role = "admin" | "user";

export const useAuth = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState<Role | null>(null);
  const [userID, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const { myAccount, auth: authPath } = usePaths();
  const { removeAll: clearLocalStorage } = useLocalStorage();

  const redirect = process.env.REACT_APP_BASE_REDIRECT_URL + myAccount;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const role = session?.user.user_metadata.role;
      setRole(role ?? "user");
      const id = session?.user.id;
      setUserId(id ?? null);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);

      const id = session?.user.id;
      setUserId(id ?? null);

      const role = session?.user.user_metadata.role;
      setRole(role ?? "user");

      const email = session?.user.email;
      setEmail(email ?? null);

      // Might be needed to get profile data later on.
      // if (session?.user) {
      //   supabase
      //     .from("profiles")
      //     .select("role")
      //     .eq("id", session.user.id)
      //     .single()
      //     .then(({ data }) => {
      //       setRole(data?.role ?? null);
      //     });
      // }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    setRole(null);
    setUserId(null);
    setSession(null);
    clearLocalStorage();
    await supabase.auth.signOut();
  };

  const changeEmailAddress = async (newEmail: string) => {
    await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: redirect }
    );
  };

  const changePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      throw error;
    }
  };
  const resetPassword = async () => {
    await supabase.auth.resetPasswordForEmail(email!, {
      redirectTo: process.env.REACT_APP_BASE_REDIRECT_URL + authPath,
    });
  };

  const requestAccountDeletion = async () => {
    if (!userID) throw new Error("No user is currently signed in.");
    const { error } = await supabase
      .from("profiles")
      .update({ marked_for_removal: new Date().toISOString() })
      .eq("id", userID);
    fetchUserProfile();
    if (error) throw error;
  };

  const fetchUserProfile = async () => {
    if (!userID) throw new Error("No user is currently signed in.");
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userID)
      .single();

    if (error) throw error;
    const profile = data as UserProfile;
    setUserProfile(profile);
    return profile;
  };

  return {
    session,
    isLoading,
    client: supabase,
    isUserAuthenticated: session !== null,
    signOut,
    role,
    userID,
    email,
    changeEmailAddress,
    changePassword,
    resetPassword,
    requestAccountDeletion,
    userProfile,
    fetchUserProfile,
  };
};
